import LogRocket from 'logrocket';
import { isNotNullOrUndefined } from './objectUtils';

const environmentName = process.env.REACT_APP_ENVIRONMENT;

const REDACTED_NAME = '[REDACTED_NAME]';
const REDACTED_EMAIL = '[REDACTED_EMAIL]';
const REDACTED_PHONE = '[REDACTED_PHONE]';
const REDACTED_ADDRESS = '[REDACTED_ADDRESS]';
const CIRCULAR_REFERENCE = '[Circular]';

const NAME_KEYS = new Set([
    'name',
    'firstname',
    'lastname',
    'fullname',
    'patientname',
    'providername',
    'customername'
]);
const EMAIL_KEYS = new Set(['email', 'emailaddress']);
const PHONE_KEYS = new Set(['phone', 'phonenumber', 'mobile', 'mobilephone']);
// `state` is deliberately absent: it is the most common key name in the app (redux state,
// subscription/order status) and is not a Safe Harbor identifier on its own.
const ADDRESS_KEYS = new Set([
    'address',
    'streetaddress1',
    'streetaddress2',
    'city',
    'postalcode',
    'zip',
    'shippingaddress',
    'billingaddress'
]);

// keys that carry no PHI but are what makes a log correlatable to a record — exempt from an
// ancestor's forced redaction so a redacted address does not take the document id with it.
const STRUCTURAL_KEYS = new Set(['id', 'ids', 'type', 'status', 'ref', 'refid', 'key', 'slug']);

const isStructuralKey = (normalizedKey) => STRUCTURAL_KEYS.has(normalizedKey)
    || normalizedKey.endsWith('id')
    || normalizedKey.endsWith('ids')
    || normalizedKey.endsWith('refid')
    || normalizedKey.endsWith('type')
    || normalizedKey.endsWith('status');

const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
// requires a separator, parentheses, or an explicit +1 so a bare ten-digit run — an epoch
// millisecond timestamp, an order number — is not rewritten as a phone number.
const PHONE_PATTERN = /(?:\+1[\s.-]?\d{3}[\s.-]?\d{3}[\s.-]?\d{4}|\(\d{3}\)[\s.-]?\d{3}[\s.-]?\d{4}|\b\d{3}[\s.-]\d{3}[\s.-]\d{4})\b/g;
const STREET_ADDRESS_PATTERN = /\b\d{1,6}\s+(?:[A-Za-z0-9.'#-]+\s+){0,6}(?:st|street|ave|avenue|rd|road|blvd|boulevard|dr|drive|ln|lane|ct|court|pl|place|way|cir|circle|pkwy|parkway|hwy|highway|ter|terrace|trl|trail)\b\.?/gi;

const getNormalizedKey = (key) => String(key || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const getRedactionTokenForKey = (key) => {
    const normalizedKey = getNormalizedKey(key);

    if (NAME_KEYS.has(normalizedKey)
        || normalizedKey.endsWith('firstname')
        || normalizedKey.endsWith('lastname')
        || normalizedKey.endsWith('fullname')) return REDACTED_NAME;
    if (EMAIL_KEYS.has(normalizedKey)) return REDACTED_EMAIL;
    if (PHONE_KEYS.has(normalizedKey)) return REDACTED_PHONE;
    if (ADDRESS_KEYS.has(normalizedKey)) return REDACTED_ADDRESS;

    return null;
};

const sanitizeStringValue = (value) => value
    .replace(EMAIL_PATTERN, REDACTED_EMAIL)
    .replace(PHONE_PATTERN, REDACTED_PHONE)
    .replace(STREET_ADDRESS_PATTERN, REDACTED_ADDRESS);

const sanitizePhiDataInternal = (value, seen, forcedRedactionToken = null) => {
    if (value === null || value === undefined) return value;

    if (forcedRedactionToken && (typeof value === 'string' || typeof value === 'number')) {
        return forcedRedactionToken;
    }

    if (typeof value === 'string') return sanitizeStringValue(value);
    if (typeof value !== 'object') return value;
    if (value instanceof Date) return value;

    if (seen.has(value)) return CIRCULAR_REFERENCE;
    seen.add(value);

    if (Array.isArray(value)) {
        const sanitizedArray = value.map((item) => sanitizePhiDataInternal(item, seen, forcedRedactionToken));
        seen.delete(value);
        return sanitizedArray;
    }

    const sanitizedObject = Object.entries(value).reduce((sanitizedValue, [key, fieldValue]) => {
        const redactionToken = getRedactionTokenForKey(key);
        const inheritedToken = isStructuralKey(getNormalizedKey(key)) ? null : forcedRedactionToken;
        sanitizedValue[key] = sanitizePhiDataInternal(fieldValue, seen, redactionToken || inheritedToken);
        return sanitizedValue;
    }, {});
    seen.delete(value);
    return sanitizedObject;
};

export const sanitizePhiData = (value) => sanitizePhiDataInternal(value, new WeakSet());

const logReduxErrorEvent = (error, errorMessage, writeToConsole = false) => {
    try {
        if (isNotNullOrUndefined(error)) {
            LogRocket.captureException(error, { extra: { severity: 'Error', environment: environmentName, eventName: 'REDUX_ERROR', message: sanitizePhiData(errorMessage) } });
        }
    } catch (ex) {
        // console.log(ex);
    }

    if (writeToConsole === true || environmentName !== 'PROD') {
        try {
            console.log(errorMessage);
            console.log(error);
        } catch (ex) {
            // console.log(ex);
        }
    }
};

const logErrorEvent = (eventName, error, errorMessage, writeToConsole = false) => {
    try {
        if (isNotNullOrUndefined(error)) {
            LogRocket.captureException(error, { extra: { severity: 'Error', environment: environmentName, eventName: eventName, message: sanitizePhiData(errorMessage) } });
        }
    } catch (ex) {
        // console.log(ex);
    }

    if (writeToConsole === true || environmentName !== 'PROD') {
        try {
            console.log(errorMessage);
            console.log(error);
        } catch (ex) {
            // console.log(ex);
        }
    }
};

const logEvent = (eventName, message, writeToConsole = false) => {
    try {
        const safeMessage = sanitizePhiData(message);
        LogRocket.captureMessage(`${eventName}: ${safeMessage}`, { extra: { severity: 'Log', environment: environmentName, eventName: eventName, message: safeMessage } });
    } catch (ex) {
        // console.log(ex);
    }

    if (writeToConsole === true || environmentName !== 'PROD') {
        try {
            console.log(message);
        } catch (ex) {
            // console.log(ex);
        }
    }
};

const logDebugEvent = (eventName, message, writeToConsole = false) => {
    try {
        const safeMessage = sanitizePhiData(message);
        LogRocket.captureMessage(`${eventName}: ${safeMessage}`, { extra: { severity: 'Debug', environment: environmentName, eventName: eventName, message: safeMessage } });
    } catch (ex) {
        // console.log(ex);
    }

    if (writeToConsole === true || environmentName !== 'PROD') {
        try {
            console.log(message);
        } catch (ex) {
            // console.log(ex);
        }
    }
};

// logrocket shows a captured exception's event name only as data on the issue; an alert is built on a chart, and a
// chart can count tracked events
const trackEvent = (eventName, properties = {}) => {
    try {
        LogRocket.track(eventName, sanitizePhiData(properties));
    } catch (ex) {
        // console.log(ex);
    }
};

const logger = {
    logReduxErrorEvent,
    logErrorEvent,
    logEvent,
    logDebugEvent,
    trackEvent,
    sanitizePhiData
};

export default logger;
